import { isBrandId } from './brand';
import { computeBrandTokens } from './brandTokens';
import { isPlainObject } from './isPlainObject';
import type {
  BrandFile,
  BrandInputs,
  BrandScheme,
  BrandTokens,
} from './types/BrandFile';

export const BRAND_FILE_FORMAT = 'ph-brand';
export const BRAND_FILE_FORMAT_VERSION = 2;

const TOKEN_TEXT_KEYS = [
  'primary',
  'onPrimary',
  'secondary',
  'onSecondary',
  'background',
  'backgroundImage',
  'surface',
  'primarySurface',
  'text',
  'textSecondary',
  'textDisabled',
  'textTertiary',
  'shadowLift',
] as const satisfies readonly (keyof BrandTokens)[];

const TOKEN_NUMBER_KEYS = [
  'radius',
  'cardRadius',
] as const satisfies readonly (keyof BrandTokens)[];

type BrandFileSource = {
  brandId: string;
  sponsorName: string;
  inputs: BrandInputs;
  logoUrl?: string;
};

/** The brand file of a published brand: its inputs, every light token and
 *  its logo. */
export const buildBrandFile = ({
  brandId,
  sponsorName,
  inputs,
  logoUrl,
}: BrandFileSource): BrandFile => ({
  format: BRAND_FILE_FORMAT,
  formatVersion: BRAND_FILE_FORMAT_VERSION,
  brandId,
  sponsorName,
  light: {
    inputs,
    tokens: computeBrandTokens(inputs),
    ...(logoUrl !== undefined && { logo: { url: logoUrl } }),
  },
});

const isBrandScheme = (value: unknown): value is BrandScheme => {
  if (!isPlainObject(value)) return false;
  const { inputs, tokens, logo } = value;
  return (
    isPlainObject(inputs) &&
    isPlainObject(inputs['colors']) &&
    isPlainObject(tokens) &&
    TOKEN_TEXT_KEYS.every(key => typeof tokens[key] === 'string') &&
    TOKEN_NUMBER_KEYS.every(key => typeof tokens[key] === 'number') &&
    (logo === undefined ||
      (isPlainObject(logo) && typeof logo['url'] === 'string'))
  );
};

/** A format 2 brand file with a complete `light` scheme. */
export const isBrandFile = (value: unknown): value is BrandFile =>
  isPlainObject(value) &&
  value['format'] === BRAND_FILE_FORMAT &&
  value['formatVersion'] === BRAND_FILE_FORMAT_VERSION &&
  isBrandId(value['brandId']) &&
  typeof value['sponsorName'] === 'string' &&
  isBrandScheme(value['light']) &&
  (value['dark'] === undefined || isBrandScheme(value['dark']));
